import * as React from 'react';
import { theme, UploadFile } from 'antd';
import { UploadChangeParam } from 'antd/es/upload';
import { RcFile } from 'antd/lib/upload';
import { readableBytes } from '@utils/common';
import { fileLogos, getFileContent } from '@utils/file';
import { uploadFilesApi } from '@api/upload';
import { UploadFileType } from '@constants/constant';

export enum Status {
  Success = 'success',
  Error = 'error',
  Rest = 'rest',
  Loading = 'loading',
}
interface OnProgress {
  onProgress: (_: number) => void;
}

export type FileWithBackendId = {
  id: string;
  file: UploadFile<any>;
  state: Status | null;
  idx: number | null;
  progress: number;
  controller?: AbortController;
  size: string;
  canPreview: boolean;
  type: UploadFileType;
  logo: ReturnType<typeof fileLogos>[keyof ReturnType<typeof fileLogos>];
  data: Awaited<ReturnType<typeof getFileContent>>;
};

const { useToken } = theme;

export const useUpload = () => {
  const localIdx = React.useRef(0);
  const { token } = useToken();

  const [files, setFiles] = React.useState<FileWithBackendId[]>([]);

  const logos = fileLogos(token);

  const fileRequest = (
    file: UploadFile,
    onProgress?: (_: number) => void,
    controller?: AbortController,
  ) => {
    const form = new FormData();
    form.append('file_name', file.name);
    form.append('file', file.originFileObj as RcFile);

    const promise = uploadFilesApi(
      form,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onProgress,
        signal: controller?.signal,
      },
      'temporary_files',
    );

    return promise;
  };

  const syncUploads = React.useCallback(async (newFiles: (FileWithBackendId & OnProgress)[]) => {
    newFiles.forEach(async (file) => {
      setFiles((prev) =>
        prev.map((f) => (f.idx === file.idx ? { ...f, state: Status.Loading } : f)),
      );

      try {
        const result = await fileRequest(file.file, file.onProgress, file?.controller);
        setFiles((prev) =>
          prev.map((f) =>
            f.idx === file.idx
              ? { ...f, state: Status.Success, id: result?.data?.file_id ?? '' }
              : f,
          ),
        );
      } catch (error) {
        setFiles((prev) =>
          prev.map((f) =>
            f.idx === file.idx ? { ...f, state: Status.Error, id: String(file.idx) } : f,
          ),
        );
      }
    });
  }, []);

  const handleProgress = (idx: number) => (progress: number) => {
    setFiles((prev) =>
      prev.map((file) => {
        return file?.idx === idx ? { ...file, progress } : file;
      }),
    );
  };

  const onFileUpload = React.useCallback(
    async (info: UploadChangeParam<UploadFile<File>>) => {
      const { fileList } = info;

      const newFiles = await Promise.all(
        fileList
          .filter((f) => f.uid.startsWith('rc-upload'))
          .map(async (file) => {
            const idx = localIdx.current;
            localIdx.current += 1;
            const controller = new AbortController();
            const ext = file.name.split('.').pop() as UploadFileType;
            const bytes = file.originFileObj?.size ?? file.size ?? 0;
            const data = (await getFileContent(file).catch(() => ({
              isImage: false,
              content: Promise.reject<string | ArrayBuffer>(),
            }))) as FileWithBackendId['data'];

            return {
              id: '',
              state: Status.Rest,
              file,
              progress: 0,
              idx,
              controller,
              onProgress: handleProgress(idx),
              size: readableBytes(bytes),
              logo: logos[ext],
              canPreview: data.isImage,
              type: ext,
              data,
            };
          }),
      );

      setFiles((prev) => prev.concat(newFiles));

      await syncUploads(newFiles);
    },
    [syncUploads, logos],
  );

  const onRemoveFile = ({ id, idx }: Pick<FileWithBackendId, 'idx' | 'id'>) => {
    setFiles((prev) =>
      prev.filter((file) => {
        const isFile = file.id === id && file.idx === idx;

        if (isFile) {
          file?.controller?.abort();
        }
        return !isFile;
      }),
    );
  };

  const clearAllFiles = () => {
    setFiles([]);
  };

  return {
    onFileUpload,
    onRemoveFile,
    files,
    clearAllFiles,
  };
};
